/**
 * PATH: apps/ai/src/main.tsx
 * PURPOSE: Entry point for AI Hub application
 * ROLE IN ARCHITECTURE: Bootstraps React app with providers
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ApiProvider } from '@quotech/api';
import { installChunkErrorHandler, installClientErrorReporter } from '@quotech/ui';
import {
  getAuthHeadersForDiagnostics,
  getOrganisationIdForDiagnostics,
} from '@quotech/auth';
import App from './App';
import { setupAIApi } from './lib/setupApi';
import './index.css';

// Handle stale chunk 404s after deployment (auto-reload)
installChunkErrorHandler();

// Capture runtime errors + failed fetches to /api/diagnostics/client-error/
// so support agents can root-cause opaque crashes without DevTools.
// See packages/ui/src/diagnostics/clientErrorReporter.ts.
installClientErrorReporter({
  buildSha: import.meta.env.VITE_BUILD_SHA,
  getAuthHeaders: getAuthHeadersForDiagnostics,
  getOrganisationId: getOrganisationIdForDiagnostics,
});

// =============================================================================
// Auth-Based State Cleanup - Clear AI state when user is not authenticated
// =============================================================================
// Since localStorage is domain-specific but auth cookies are shared across
// subdomains, we need to check auth state on load and clear stale AI context.
// ALSO clears the in-memory orchestrator context which stores activeJobId.
import { getAuthCookie } from '@quotech/auth';
import { aiOrchestrator } from './services/aiAgents';

const LOGOUT_EVENT_KEY = 'quotech:logout';
const AI_STORAGE_KEYS = ['quotech-ai', 'ai-hub-conversation', 'ai-hub-job-context'];
const LAST_USER_KEY = 'ai-hub-last-user';

function clearAIState() {
  AI_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  // Also clear the in-memory orchestrator context (stores activeJobId)
  try {
    (aiOrchestrator as Record<string, unknown>).contextStore?.clear?.();
  } catch (e) {
    console.warn('[AI Hub] Could not clear orchestrator context:', e);
  }
}

// Check if current user matches the last user who used AI Hub
// This handles cross-subdomain logout and different user login scenarios
function checkAuthStateAndClearIfNeeded() {
  const token = getAuthCookie();
  const lastUser = localStorage.getItem(LAST_USER_KEY);
  
  if (!token) {
    // No auth token - user logged out (possibly from another subdomain)
    clearAIState();
    localStorage.removeItem(LAST_USER_KEY);
    return;
  }
  
  // Decode JWT to get user info (without verification)
  try {
    const parts = token.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const currentUser = payload.user_id || payload.sub || payload.email || '';
      
      if (lastUser && lastUser !== String(currentUser)) {
        // Different user logged in - clear previous user's AI state
        clearAIState();
      }
      
      // Store current user for next check
      if (currentUser) {
        localStorage.setItem(LAST_USER_KEY, String(currentUser));
      }
    }
  } catch (e) {
    // Token decode failed - clear to be safe
    clearAIState();
  }
}

// Run auth check immediately on load
checkAuthStateAndClearIfNeeded();

// Same-tab logout event
window.addEventListener('quotech:logout', clearAIState);

// Cross-tab logout event (via localStorage)
window.addEventListener('storage', (event) => {
  if (event.key === LOGOUT_EVENT_KEY) {
    clearAIState();
    localStorage.removeItem(LAST_USER_KEY);
    // Force reload to ensure clean state
    window.location.reload();
  }
});

// =============================================================================
// First-run initialization: Default sidebar to collapsed for AI Hub
// =============================================================================
// This runs ONCE per browser on first AI Hub visit.
// Sets the shared `sidebar-collapsed` key to 'true' so the global sidebar
// starts collapsed, giving more space to the AI chat interface.
// Users who have already set a preference won't be affected.
const AI_SIDEBAR_INIT_KEY = 'ai-hub-sidebar-initialized';
if (!localStorage.getItem(AI_SIDEBAR_INIT_KEY)) {
  localStorage.setItem('sidebar-collapsed', 'true');
  localStorage.setItem(AI_SIDEBAR_INIT_KEY, 'true');
}

// Initialize the API client with AI-specific configuration
setupAIApi();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ApiProvider>
      {/* unstable_useTransitions={false} — see apps/newmark/src/main.tsx. (qf!3010, May 2026) */}
      <BrowserRouter unstable_useTransitions={false}>
        <App />
      </BrowserRouter>
    </ApiProvider>
  </React.StrictMode>
);

